import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { StatefulDataTable } from "baseui/data-table";
import { StringColumn, NumericalColumn, CustomColumn } from "baseui/data-table";
import { Button } from "baseui/button";
import { Avatar } from "baseui/avatar";
import { StatefulInput } from "baseui/input";
import { Spinner } from "baseui/spinner";
import { Card, StyledBody } from "baseui/card";
import { useStyletron } from "baseui";
import { Grid, Cell, BEHAVIOR } from "baseui/layout-grid";
import { Block } from "baseui/block";
import { HeadingLarge, HeadingMedium } from "baseui/typography";
import { marked } from "marked";

const SerpResults = () => {
	const [data, setData] = useState([]);
	const [aiSummary, setAiSummary] = useState("");
	const [aiSuggestions, setAiSuggestions] = useState("");
	const [search, setSearch] = useState("");
	const [loadingTable, setLoadingTable] = useState(true);
	const [loadingAI, setLoadingAI] = useState(false);
	const [css] = useStyletron();
	const apiUrl = process.env.REACT_APP_API_URL;
	const [searchTerm, setSearchTerm] = useState("");
	const [loading, setLoading] = useState(false);
	const user = useSelector((state) => state.user);
	// console.log("user", user);
	// ✅ Convert Markdown (###, **, etc.) to HTML
	const formatAsHTML = (text) => {
		if (!text) return "<p>Loading AI insights...</p>";
		return marked(text);
	};

	// 🟢 Load the table data ONCE when the page loads
	useEffect(() => {
		const fetchTableData = async () => {
			try {
				const res = await axios.get(`${apiUrl}/api/serp-results`);
				setData(res.data.serp_results); // Load SERP results
				setLoadingTable(false);
			} catch (err) {
				console.error("Error fetching table data:", err);
				setLoadingTable(false);
			}
		};

		fetchTableData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// 🟡 Fetch AI insights when a location button is clicked
	const fetchAIData = async (selectedLocation) => {
		setLoadingAI(true);
		try {
			const res = await axios.get(`${apiUrl}/api/serp-results/${selectedLocation}`);
			setAiSummary(formatAsList(res.data.summary));  // ✅ Correct field name
			setAiSuggestions(formatAsList(res.data.suggestions));  // ✅ Correct field name
		} catch (err) {
			console.error("Error fetching AI data:", err);
			setAiSummary("<p>Error loading AI insights.</p>");
			setAiSuggestions("<p>Error loading AI recommendations.</p>");
		}
		setLoadingAI(false);
	};

	// ✅ Function to format AI-generated text into an unordered list
	const formatAsList = (text) => {
		if (!text) return "<p>Loading AI insights...</p>";

		const items = text.split(/\d+\.\s|\•\s/).filter(item => item.trim() !== "");
		return `<ul>${items.map(item => `<li>${item.trim()}</li>`).join("")}</ul>`;
	};

	// 🔍 Search filter logic for the table
	const filteredData = data.filter((row) => {
		const searchText = search.toLowerCase();
		return (
			row.title.toLowerCase().includes(searchText) ||
			row.snippet.toLowerCase().includes(searchText) ||
			row.location.toLowerCase().includes(searchText) ||
			(row.json_data?.source || "").toLowerCase().includes(searchText)
		);
	});

	// 🟢 Handle New Search Submission
	const handleNewSearch = async () => {
		if (!searchTerm.trim()) {
			alert("Please enter a search term.");
			return;
		}

		setLoading(true);
		try {
			const res = await axios.post(`${apiUrl}/api/scraper/search`, { searchTerm });
			console.log("Scraper Response:", res.data);
			fetchTableData(); // Reload table data
		} catch (err) {
			console.error("Error running scraper:", err);
		}
		setLoading(false);
	};
	// 🟢 Fetch SERP results from the database
	const fetchTableData = useCallback(async () => {
		setLoading(true);
		try {
			const res = await axios.get(`${apiUrl}/api/serp-results`);
			setData(res.data.serp_results);
		} catch (err) {
			console.error("Error fetching table data:", err);
		}
		setLoading(false);
	}, [apiUrl]);

	// Load table data when the component mounts
	useEffect(() => {
		fetchTableData();
	}, [fetchTableData]);
	// ✅ Define table columns
	const columns = [
		CustomColumn({
			title: "Favicon",
			mapDataToValue: (row) => row.json_data?.favicon || "",
			renderCell: ({ value }) =>
				value ? <Avatar name="Site" size="scale1000" src={value} /> : "N/A",
		}),
		StringColumn({
			title: "Date",
			mapDataToValue: (row) => new Date(row.created_at).toLocaleString(),
		}),
		StringColumn({
			title: "Search Term",
			mapDataToValue: (row) => row.search_term || "N/A",
		}),
		StringColumn({
			title: "Location",
			mapDataToValue: (row) => row.location,
		}),
		NumericalColumn({
			title: "Position",
			mapDataToValue: (row) => row.json_data?.position || 0,
		}),
		StringColumn({
			title: "Page Title",
			mapDataToValue: (row) => row.title,
		}),
		StringColumn({
			title: "Meta Description",
			mapDataToValue: (row) => row.snippet,
		}),
		StringColumn({
			title: "Source",
			mapDataToValue: (row) => row.json_data?.source || "N/A",
		}),
		CustomColumn({
			title: "Link",
			mapDataToValue: (row) => row.link,
			renderCell: ({ value }) => (
				<Button kind="secondary" size="compact" onClick={() => window.open(value, "_blank")}>
					View
				</Button>
			),
		}),
	];

	// ✅ Ensure rows are formatted properly
	const rows = filteredData.map((row, index) => ({
		id: String(index),
		data: row,
	}));

	return (
		<Block padding="20px">
			<HeadingLarge>📊 Google Search Rankings</HeadingLarge>
			{/* Search Input & Submit Button */}
			<Grid behavior={BEHAVIOR.fluid}>
				<Cell span={[12, 6, 6]}>
					<StatefulInput
						placeholder="Enter search term..."
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						clearable
					/>
				</Cell>
				<Cell span={[12, 6, 6]}>
					<Button onClick={handleNewSearch} isLoading={loading}>
						🔍 Run New Search
					</Button>
				</Cell>
			</Grid>
			{/* Location Selector for AI Insights */}
			<Grid behavior={BEHAVIOR.fluid}>
				<Cell span={12}>
					<Button onClick={() => fetchAIData("D'Iberville, Mississippi, United States")}>
						D'Iberville, MS
					</Button>
					<Button onClick={() => fetchAIData("Atlanta, GA")}>
						Atlanta, GA
					</Button>
					<Button onClick={() => fetchAIData("Birmingham, AL")}>
						Birmingham, AL
					</Button>
					<Button onClick={() => fetchAIData("Montgomery, AL")}>
						Montgomery, AL
					</Button>
				</Cell>
			</Grid>

			{/* AI Summary & Suggestions */}
			{loadingAI ? (
				<Spinner />
			) : aiSummary && aiSuggestions ? (
				<Grid behavior={BEHAVIOR.fluid}>
					<Cell span={[12, 6, 6]}>
						<Card overrides={{ Root: { style: { marginBottom: "20px", backgroundColor: "#F8F9FA", maxHeight: "800px", overflowY: "auto" } } }}>
							<StyledBody>
								<HeadingMedium>📌 AI Summary</HeadingMedium>
								<div dangerouslySetInnerHTML={{ __html: formatAsHTML(aiSummary) }} />
							</StyledBody>
						</Card>
					</Cell>
					<Cell span={[12, 6, 6]}>
						<Card overrides={{ Root: { style: { marginBottom: "20px", backgroundColor: "#F8F9FA", maxHeight: "800px", overflowY: "auto" } } }}>
							<StyledBody>
								<HeadingMedium>💡 SEO Suggestions</HeadingMedium>
								<div dangerouslySetInnerHTML={{ __html: formatAsHTML(aiSuggestions) }} />
							</StyledBody>
						</Card>
					</Cell>
				</Grid>
			) : null}

			{/* Search Input */}
			<StatefulInput
				placeholder="🔍 Search all columns..."
				value={search}
				onChange={(e) => setSearch(e.target.value)}
				clearable
				overrides={{ Root: { style: { marginBottom: "20px" } } }}
			/>

			{/* Table */}
			{loadingTable ? (
				<Spinner />
			) : (
				<div className={css({ height: "800px", backgroundColor: "#FFFFFF", padding: "20px", borderRadius: "10px" })}>
					<StatefulDataTable columns={columns} rows={rows} rowHeight={78} />
				</div>
			)}
		</Block>
	);
};

export default SerpResults;
